import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {FormGroup, Input, Label, ListGroup, ListGroupItem} from 'reactstrap';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {IRootState} from 'app/shared/reducers';
import {getAllByType as getCountries} from 'app/entities/region/region.reducer';
import {getDispatcherDashboardCounts} from 'app/entities/patient-case/patient-case.reducer';
import {IRegion} from 'app/shared/model/region.model';
import {hasAnyPermission} from 'app/shared/auth/permissions';
import {getMMamaSettings} from 'app/modules/administration/mmama-settings/mmama-settings.reducer';

export interface ISidebarProps extends StateProps, DispatchProps, RouteComponentProps {
  loading?: boolean;
}

const Sidebar = (props: ISidebarProps) => {
  const {
    countries,
    isAdmin,
    hasCaseViewPermission,
    loading,
    dashboardCountsLoading,
    dashboardCounts,
    account,
    userCountryId,
    location,
    history,
    mmamaSettings
  } = props;
  const [activeCountry, setActiveCountry] = useState<IRegion>({});
  const [activeCaseStatus, setActiveCaseStatus] = useState<string>('OPEN');

  // Get current country settings
  const getCurrentCountrySettings = (countryId?: string) => {
    if (!mmamaSettings || !countryId) return null;
    return mmamaSettings.find(setting => setting.countryId === countryId) || null;
  };

  const countrySettings = getCurrentCountrySettings(activeCountry?.id || userCountryId);

  // Initialize state from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const statusParam = urlParams.get('status');
    const countryIdParam = urlParams.get('countryId');

    if (statusParam) {
      setActiveCaseStatus(statusParam);
    }
    if (countryIdParam && countries.length > 0) {
      const selectedCountry = countries.find(country => country.id === countryIdParam);
      if (selectedCountry && (!activeCountry?.id || activeCountry.id !== countryIdParam)) {
        setActiveCountry(selectedCountry);
      }
    }
  }, [countries, location.search]);

  useEffect(() => {
    props.getCountries();
  }, []);

  useEffect(() => {
    if (!props.mmamaSettings || props.mmamaSettings.length === 0) {
      props.getMMamaSettings();
    }
  }, []);

  // Load dashboard counts based on user role and country selection
  useEffect(() => {
    const loadDashboardCounts = (countryId?: string, dispatchCenterId?: string) => {
      if (dispatchCenterId) {
        props.getDispatcherDashboardCounts(dispatchCenterId);
      } else if (countryId) {
        props.getDispatcherDashboardCounts(undefined, countryId);
      }
    };

    if (isAdmin) {
      // For admin users: load counts when activeCountry is available
      if (activeCountry?.id) {
        loadDashboardCounts(activeCountry.id);
      }
    } else {
      // For non-admin users: prioritize dispatchCenterId, fallback to userCountryId
      if (account?.dispatchCenterId) {
        loadDashboardCounts(undefined, account.dispatchCenterId);
      } else if (userCountryId) {
        loadDashboardCounts(userCountryId);
      }
    }
  }, [activeCountry?.id, account?.dispatchCenterId, userCountryId, isAdmin]);

  const handleCaseStatusClick = (caseStatus: string) => {
    if (loading || dashboardCountsLoading) return;

    setActiveCaseStatus(caseStatus);
    // Navigate using React Router to avoid page reload
    const urlParams = new URLSearchParams(location.search);
    const currentCountryId = activeCountry?.id || urlParams.get('countryId');

    const newParams = new URLSearchParams();
    newParams.set('status', caseStatus);
    if (currentCountryId) {
      newParams.set('countryId', currentCountryId);
    }

    const url = `/dispatcher-dashboard?${newParams.toString()}`;
    history.push(url);
  };

  const handleCountrySelection = (countryId: string) => {
    if (!countryId || loading || dashboardCountsLoading) {
      if (!countryId) alert('No user country found.');
      return;
    }

    const selectedCountry: IRegion = props.countries.find(country => country.id === countryId);
    if (selectedCountry) {
      let statusToUse = activeCaseStatus;

      // Check if the new country has dispatcher feedback enabled
      const newCountrySettings = getCurrentCountrySettings(countryId);

      // If current status is PENDING_FEEDBACK and new country doesn't have feedback enabled, change to OPEN
      if (activeCaseStatus === 'PENDING_FEEDBACK' && !newCountrySettings?.enableDispatcherFeedback) {
        statusToUse = 'OPEN';
        setActiveCaseStatus('OPEN');
      }

      setActiveCountry(selectedCountry);
      // Navigate using React Router to avoid page reload
      const newParams = new URLSearchParams();
      newParams.set('countryId', selectedCountry.id);
      if (statusToUse) {
        newParams.set('status', statusToUse);
      }

      const url = `/dispatcher-dashboard?${newParams.toString()}`;
      history.push(url);
    }
  };

  useEffect(() => {
    if (countries.length > 0 && isAdmin) {
      // For admin users, set first country as default only if no country is already selected
      const currentParams = new URLSearchParams(location.search);
      const currentCountryId = currentParams.get('countryId');

      if (!currentCountryId && !activeCountry?.id) {
        const firstCountry = countries[0];
        if (firstCountry) {
          setActiveCountry(firstCountry);
          // Navigate to dashboard with first country selected for admin users
          const currentPath = location.pathname;
          if (currentPath === '/dispatcher-dashboard') {
            const newParams = new URLSearchParams();
            newParams.set('countryId', firstCountry.id);
            newParams.set('status', 'OPEN');

            const newUrl = `/dispatcher-dashboard?${newParams.toString()}`;
            history.replace(newUrl);
          }
        }
      }
    }
  }, [countries, isAdmin]);

  const regCountriesList = countries.filter(ele => {
    return isAdmin ? 1 : ele.id === activeCountry?.id;
  });

  return (
    <div className="dispatcher-sidebar p-3">
      <h5 className="mb-4" onClick={() => history.push('/dispatcher-dashboard')}>
        Dispatcher Dashboard
      </h5>

      {isAdmin && (
        <FormGroup className="mb-4">
          <Label for="sidebar-countries">Country</Label>
          <Input
            id="sidebar-countries"
            type="select"
            className="form-control"
            name="sidebarCountry"
            disabled={loading || dashboardCountsLoading}
            onChange={(e: any) => handleCountrySelection(e.target.value)}
            value={activeCountry?.id}
          >
            {regCountriesList &&
              regCountriesList.map(country => (
                <option value={country.id} key={country.id}>
                  {country.name}
                </option>
              ))}
          </Input>
        </FormGroup>
      )}

      <ListGroup flush>
        <ListGroupItem
          className={`d-flex justify-content-between align-items-center ${activeCaseStatus === 'OPEN' ? 'active' : ''}`}
          onClick={() => handleCaseStatusClick('OPEN')}
          style={{
            cursor: loading || dashboardCountsLoading ? 'not-allowed' : 'pointer',
            opacity: loading || dashboardCountsLoading ? 0.6 : 1
          }}
        >
          <div><FontAwesomeIcon icon="user" className="mr-2"/> Open Cases</div>
          <span className="badge badge-danger dispatcher-badge">{dashboardCounts?.openCases || 0}</span>
        </ListGroupItem>
        <ListGroupItem
          className={`d-flex justify-content-between align-items-center ${activeCaseStatus === 'READY_FOR_PAYMENT' ? 'active' : ''}`}
          onClick={() => handleCaseStatusClick('READY_FOR_PAYMENT')}
          style={{
            cursor: loading || dashboardCountsLoading ? 'not-allowed' : 'pointer',
            opacity: loading || dashboardCountsLoading ? 0.6 : 1
          }}
        >
          <div><FontAwesomeIcon icon="money-check" className="mr-2"/> Ready For Payment</div>
          <span className="badge badge-secondary dispatcher-badge">{dashboardCounts?.readyForPayment || 0}</span>
        </ListGroupItem>
        <ListGroupItem
          className={`d-flex justify-content-between align-items-center ${activeCaseStatus === 'FOLLOW_UP' ? 'active' : ''}`}
          onClick={() => handleCaseStatusClick('FOLLOW_UP')}
          style={{
            cursor: loading || dashboardCountsLoading ? 'not-allowed' : 'pointer',
            opacity: loading || dashboardCountsLoading ? 0.6 : 1
          }}
        >
          <div><FontAwesomeIcon icon="clipboard-check" className="mr-2"/> Follow-up</div>
          <span className="badge badge-secondary dispatcher-badge">{dashboardCounts?.followUp || 0}</span>
        </ListGroupItem>
        <ListGroupItem
          className={`d-flex justify-content-between align-items-center ${activeCaseStatus === 'PENDING_PAYMENT' ? 'active' : ''}`}
          onClick={() => handleCaseStatusClick('PENDING_PAYMENT')}
          style={{
            cursor: loading || dashboardCountsLoading ? 'not-allowed' : 'pointer',
            opacity: loading || dashboardCountsLoading ? 0.6 : 1
          }}
        >
          <div><FontAwesomeIcon icon="money-check" className="mr-2"/> Pending Payment</div>
          <span className="badge badge-secondary dispatcher-badge">{dashboardCounts?.pendingPayment || 0}</span>
        </ListGroupItem>
        {countrySettings?.enableDispatcherFeedback && (
          <ListGroupItem
            className={`d-flex justify-content-between align-items-center ${activeCaseStatus === 'PENDING_FEEDBACK' ? 'active' : ''}`}
            onClick={() => handleCaseStatusClick('PENDING_FEEDBACK')}
            style={{
              cursor: loading || dashboardCountsLoading ? 'not-allowed' : 'pointer',
              opacity: loading || dashboardCountsLoading ? 0.6 : 1
            }}
          >
            <div><FontAwesomeIcon icon="comment" className="mr-2"/> Feedback</div>
            <span className="badge badge-secondary dispatcher-badge">{dashboardCounts?.pendingFeedback || 0}</span>
          </ListGroupItem>
        )}
        {(hasCaseViewPermission || isAdmin) &&
          <ListGroupItem onClick={() => history.push('/case-management')} style={{cursor: 'pointer'}}>
            <FontAwesomeIcon icon="search" className="mr-2"/> Search
          </ListGroupItem>
        }
      </ListGroup>
    </div>
  );
};

const mapStateToProps = ({authentication, region, patientCase, mmamaSettings}: IRootState) => ({
  countries: region.countries,
  isAdmin: hasAnyPermission(authentication.account.roles, ['ADMIN']),
  account: authentication.account,
  userCountryId: authentication.userCountryId,
  dashboardCounts: patientCase.dashboardCounts,
  dashboardCountsLoading: patientCase.dashboardCountsLoading,
  hasCaseViewPermission: hasAnyPermission(authentication.permissions, ['CASE_VIEW']),
  mmamaSettings: mmamaSettings.settings,
});

const mapDispatchToProps = {
  getCountries,
  getDispatcherDashboardCounts,
  getMMamaSettings,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Sidebar));


